// The ShariFly API client: the types come from `schema.d.ts`, generated by
// `pnpm gen:api` from saf-api's OpenAPI document; never edit that file.
// `createApi` returns an openapi-fetch client whose paths, bodies and
// responses are typed from the document.

import createClient, { type ClientOptions, type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type { components, paths };

/** The error envelope every failure carries (`{ error: { code, message, details } }`). */
export type ErrorBody = components["schemas"]["ErrorBody"];
export type ErrorDetail = components["schemas"]["ErrorDetail"];

/** Narrows an unknown failure body to the envelope's detail. */
export function errorDetail(body: unknown): ErrorDetail | undefined {
  if (body && typeof body === "object" && "error" in body) {
    const e = (body as { error?: unknown }).error;
    if (e && typeof e === "object" && "code" in e && "message" in e) {
      return e as ErrorDetail;
    }
  }
  return undefined;
}

export interface ApiOptions extends ClientOptions {
  /** Companion bearer token; the web app relies on the session cookie instead. */
  bearer?: string;
  /** Called on every 401, so a UI can drop its signed-in state. */
  onUnauthorized?: () => void;
}

/**
 * A client for `/api/v1`. Cookies travel with every request (the web session
 * is an httpOnly cookie), so no token is ever kept by the caller.
 */
export function createApi(options: ApiOptions = {}) {
  const { bearer, onUnauthorized, ...rest } = options;
  const client = createClient<paths>({
    baseUrl: "",
    credentials: "same-origin",
    ...rest,
  });
  const middleware: Middleware = {
    onRequest({ request }) {
      if (bearer) request.headers.set("authorization", `Bearer ${bearer}`);
      if (!request.headers.has("accept")) request.headers.set("accept", "application/json");
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401 && onUnauthorized) onUnauthorized();
      return response;
    },
  };
  client.use(middleware);
  return client;
}

export type Api = ReturnType<typeof createApi>;
